import { useCallback, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { CaretLeft, MagnifyingGlass } from "@phosphor-icons/react";
import { Step } from "./constants";
import { Input } from "@/components/ui/input";
import { DrawerHeader } from "@/components/ui/drawer";
import { useTranslation } from "react-i18next";
import { Currency, Vessel } from "@/lib/types";
import Loading from "@/components/custom/loading";
import { useGetExchangeRatesByDataRange } from "@/services/queries/exchange-rates";
import { addDays, format } from "date-fns";
import { currencyFormatter } from "@/lib/utils";
import { InvoiceFormValues } from "./index";

const currencyIcons = {
  EUR: "/svgs/europe.svg",
  USD: "/svgs/usa.svg",
  GBP: "/svgs/england.svg",
  TRY: "/svgs/turkey.svg",
  BTC: "/svgs/btc.svg",
};

const CurrencyStep = ({
  currencies,
  isCurrenciesPending,
  form,
  setStep,
  vessels,
}: {
  currencies: Currency[];
  isCurrenciesPending: boolean;
  form: UseFormReturn<InvoiceFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  vessels: Vessel[];
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { data: exchangeRatesData } = useGetExchangeRatesByDataRange(
    format(form.watch("issueDate") ?? new Date(), "yyyy-MM-dd"),
    format(addDays(form.watch("issueDate") ?? new Date(), 1), "yyyy-MM-dd")
  );

  const exchangeRates = useMemo(
    () => exchangeRatesData?.value ?? [],
    [exchangeRatesData]
  );

  const vessel = useMemo(
    () => vessels.find((vessel) => vessel.id === form.watch("vesselId")),
    [vessels, form]
  );

  const getReportingConversionRate = useCallback(
    (currencyId: string) => {
      if (!vessel || currencyId === vessel.defaultCurrencyId) return undefined;

      const directRate = exchangeRates.find(
        (rate) =>
          rate.fromCurrencyId === currencyId &&
          rate.toCurrencyId === vessel.defaultCurrencyId
      );

      if (directRate?.rate) return directRate.rate;

      const inverseRate = exchangeRates.find(
        (rate) =>
          rate.fromCurrencyId === vessel.defaultCurrencyId &&
          rate.toCurrencyId === currencyId
      );

      return inverseRate?.rate ? 1 / inverseRate.rate : undefined;
    },
    [exchangeRates, vessel]
  );

  const currenciesWithExchangeRates = useMemo(() => {
    return currencies.map((currency) => {
      const reportingConversionRate = getReportingConversionRate(currency.id);

      return {
        ...currency,
        exchangeRate: currencyFormatter(
          currency.id === vessel?.defaultCurrencyId
            ? 1
            : reportingConversionRate
              ? reportingConversionRate
              : undefined,
          vessel?.defaultCurrencyCode
        ),
      };
    });
  }, [
    currencies,
    getReportingConversionRate,
    vessel?.defaultCurrencyCode,
    vessel?.defaultCurrencyId,
  ]);

  const filteredCurrencies = currenciesWithExchangeRates.filter((currency) =>
    t(`currency.${currency.code.toLowerCase()}`)
      .toLocaleLowerCase()
      .includes(search.toLocaleLowerCase())
  );

  return (
    <div>
      <DrawerHeader
        title={t("title.select_currency")}
        leftButton={
          <button type="button" onClick={() => setStep(Step.INITAL)}>
            <CaretLeft color="#E3EDFC" size={18} />
          </button>
        }
        rightButton={<span className="w-[18px]" />}
      />
      <div className="relative mt-5 w-full">
        <MagnifyingGlass
          color="#719BAD"
          className="pointer-events-none absolute left-2 top-1/2 z-10 -translate-y-1/2"
          size={20}
        />
        <Input
          className="pl-10"
          placeholder={t("label.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isCurrenciesPending ? (
        <Loading className="mt-10" />
      ) : (
        <div className="mt-5 flex flex-col items-center justify-center">
          {filteredCurrencies.map((currency) => (
            <button
              data-testid={`invoice-form-currency-item-${currency.code}`}
              type="button"
              onClick={() => {
                // `conversionRate` is owned by the parent invoice-form, which
                // resolves the latest published rate for the selected pair.
                form.setValue("currencyId", currency.id);
                setStep(Step.INITAL);
              }}
              key={currency.id}
              className="flex w-full flex-row items-center justify-between gap-[12px] border-b border-white/15 py-4 text-base text-white"
            >
              <span className="flex flex-row items-center gap-[12px]">
                <img
                  src={
                    currencyIcons[currency.code as keyof typeof currencyIcons]
                  }
                  alt={currency.name}
                />
                {t(`currency.${currency.code.toLowerCase()}`)}
              </span>
              <span className="text-[15px] font-medium text-white">
                {currency.exchangeRate}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CurrencyStep;
